import CpLocation from "@assets/images/TasksMap/cp_location.png";
import BlueMarker from "@assets/images/TasksMap/pending.png";
import SearchIcon from "@assets/svg/SearchIcon";
import Image from "@components/common/Image";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import { height } from "@constants/metrics";
import { getDeliveryTasksMap } from "@services/api";
import useFocusAndAppState from "@hooks/useFocusAndAppState";
import useSarvCall from "@hooks/useSarvCall";
import { debounce } from "lodash";
import analytics from "@services/analytics";
import { MAP_VIEW_EVENTS } from "@constants/analyticsEventsCT";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  PanResponder,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { request, PERMISSIONS } from "react-native-permissions";
import MapView, { Marker, Polygon, PROVIDER_GOOGLE } from "react-native-maps";
import AnimatedPanel from "./AnimatedPanel";
import ContactModal from "./ContactModal";
import CustomMarker from "./CustomMarker";
import TagsContainer from "./TagsContainer";
import { markerSourceMap, STORAGE_KEY } from "./constants";
import { CpData } from "./interfaces";
import CallOptionsModal from "../components/CallOptionsModal";
import SarvCallModal from "../components/SarvCallModal";

function OrdersMapView({ navigation, route }: any) {
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [cpData, setCpData] = useState<CpData | null>(null);
  const [cpPolygon, setCpPolygon] = useState([]);
  const [taskActionConfig, setTaskActionConfig] = useState({});
  const [markers, setMarkers] = useState({
    pending: [],
    rto: [],
    delivered: [],
    rescheduled: [],
  });
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [selectedType, setSelectedType] = useState("all");
  const [currentDelta, setCurrentDelta] = useState(0.04);
  const [contentHeight, setContentHeight] = useState(0);
  const [contactOption, setContactOption] = useState(null);
  const [showCallOptionsModal, setShowCallOptionsModal] = useState(false);
  const [contactOpts, setContactOpts] = useState<string[]>(["DIALER"]);
  const { createCall, showSarvModal, setShowSarvModal, updateSarvData } =
    useSarvCall();
  const isRescheduledCp = useRef(false);

  useFocusAndAppState(
    () => {},
    () => {
      if (isRescheduledCp.current) {
        AsyncStorage.getItem(STORAGE_KEY).then((val) => {
          if (!val) return;
          setShowCallOptionsModal(true);
          AsyncStorage.removeItem(STORAGE_KEY);
        });
      }
    },
  );

  useEffect(() => {
    (async () => {
      await request(PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION);
    })();
  }, []);

  const onContentLayout = (event: any) => {
    const { height } = event.nativeEvent.layout;
    setContentHeight(height + 50);
  };
  const pan = useRef(new Animated.ValueXY()).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const slideIn = () => {
    slideAnim.setValue(0);
    setIsPanelVisible(true);
    pan.setValue({ x: 0, y: 0 });
    Animated.timing(slideAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  };

  const slideOut = () => {
    Animated.timing(slideAnim, {
      toValue: 0,
      duration: 500,
      useNativeDriver: true,
    }).start(() => {
      setSelectedMarker(null);
      setIsPanelVisible(false);
      pan.setValue({ x: 0, y: 0 });
    });
  };

  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (e, gestureState) => {
        // Only respond to single-finger touches
        return gestureState.numberActiveTouches === 1;
      },
      onPanResponderMove: (e, gestureState) => {
        if (gestureState.dy > 0) {
          // Only drag downwards
          pan.setValue({ x: 0, y: gestureState.dy });
        }
      },
      onPanResponderRelease: (e, gestureState) => {
        if (gestureState.dy > 100) {
          // If dragged down more than 100px, close the panel
          slideOut();
        } else {
          // Otherwise, snap back to original position
          Animated.spring(pan, {
            toValue: { x: 0, y: 0 },
            useNativeDriver: true,
          }).start();
        }
      },
    }),
  ).current;

  const fetchData = async () => {
    try {
      const payload = {
        searchTerm: searchText || "",
        filter: {
          routes: [],
          dbs: [],
        },
      };
      const { data } = await getDeliveryTasksMap(payload);
      isRescheduledCp.current = data?.isRescheduledCp || false;
      setCpData(data.cp_coords);
      setCpPolygon(data.cp_polygon_coords);
      setMarkers({
        pending: data.pending || [],
        delivered: data.deliveredToday || [],
        rto: data.rtoToday || [],
        rescheduled: data.rescheduled || [],
      });
      setTaskActionConfig(data?.task_action_config);
      updateSarvData(data);
      setContactOpts(
        Array.isArray(data?.contact_btns) ? data?.contact_btns : ["DIALER"],
      );
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    (() => {
      fetchData();
    })();
  }, [searchText]);

  const onSearchInputChange = debounce((text: string) => {
    setSearchText(text);
  }, 800);

  const onPressCrossIcon = () => {
    onSearchInputChange("");
  };

  const onPressSearchIcon = () => {
    setIsSearchEnabled(!isSearchEnabled);
  };

  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };

  const handleMarkerPress = (marker: any) => {
    setSelectedMarker(marker);
    if (marker?.user_id) {
      analytics.trackEvent(MAP_VIEW_EVENTS.MAP_MARKER_PRESS, {
        cx_id: marker.user_id,
      });
    }
    slideIn();
  };

  const calculateZoomLevel = (delta: number) => {
    const zoomLevel = Math.log2(360 / delta);
    return parseInt(`${zoomLevel}`);
  };

  const handleRegionChangeComplete = (region: any) => {
    setCurrentDelta(region.latitudeDelta);
  };

  const zoomLevel = calculateZoomLevel(currentDelta);

  const renderTags = () => (
    <TagsContainer
      markers={markers}
      selectedType={selectedType}
      setSelectedType={setSelectedType}
      slideOut={slideOut}
    />
  );
  const renderMarkers = () => {
    if (!markers) {
      return null;
    }
    return Object.keys(markers).flatMap((key) =>
      markers[key].map((val: any) => {
        if (
          !["all", key].includes(selectedType) ||
          !val.cx_lat ||
          !val.cx_lng
        ) {
          return null;
        }
        const source = markerSourceMap[key] || BlueMarker;

        return (
          <CustomMarker
            key={val.card_id}
            marker={{ ...val, source }}
            zoomLevel={zoomLevel}
            handleMarkerPress={handleMarkerPress}
          />
        );
      }),
    );
  };

  const memoizedTags = useMemo(renderTags, [selectedType, markers]);
  const memoizedMarkers = useMemo(renderMarkers, [
    selectedType,
    markers,
    zoomLevel,
  ]);

  const ifMarkersExists =
    markers.pending.length ||
    markers.delivered.length ||
    markers.rescheduled.length ||
    markers.rto.length;

  return (
    <View>
      <HeaderWithSearch
        headertitle={!isSearchEnabled ? "Map View" : "Search Map"}
        headerStyle={{ color: "#000" }}
        hideShadow={false}
        navigation={navigation}
        route={route}
        searchIcon={<SearchIcon height={20} width={20} />}
        isSearchEnabled={isSearchEnabled}
        searchPlaceholder="Search by Customer name, Address, Number"
        onPressCrossIcon={onPressCrossIcon}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        onSearchInputChange={onSearchInputChange}
        onPressSearchIcon={onPressSearchIcon}
      />
      {cpData ? (
        <View style={{ height: height - (isSearchEnabled ? 140 + 66 : 140) }}>
          <MapView
            provider={PROVIDER_GOOGLE}
            googleRenderer={"LEGACY"}
            style={StyleSheet.absoluteFill}
            mapType="terrain"
            showsMyLocationButton={true}
            minZoomLevel={2}
            maxZoomLevel={20}
            rotateEnabled={true}
            showsCompass={true}
            initialRegion={{
              latitude: cpData.latitude,
              longitude: cpData.longitude,
              latitudeDelta: 0.04,
              longitudeDelta: 0.04,
            }}
            toolbarEnabled={false}
            showsUserLocation={true}
            zoomControlEnabled={true}
            onRegionChangeComplete={handleRegionChangeComplete}
            onPress={() => {
              slideOut();
            }}
          >
            {cpPolygon.length ? (
              <Polygon
                coordinates={cpPolygon}
                fillColor="rgba(7, 7, 7, 0.02)"
                strokeColor="rgba(114, 113, 110, 0.8)"
                strokeWidth={2}
              />
            ) : null}
            <Marker
              coordinate={{
                latitude: cpData.latitude,
                longitude: cpData.longitude,
              }}
              onPress={() =>
                handleMarkerPress({
                  cx_lat: cpData.latitude,
                  cx_lng: cpData.longitude,
                  user_name: cpData.cp_name,
                  cx_formatted_address: cpData.cp_address,
                  phone_number_1: cpData.cp_phone_number,
                })
              }
            >
              <View style={{ alignItems: "center" }}>
                <Image
                  source={CpLocation}
                  style={{ height: 20, width: 20 }}
                  resizeMode="contain"
                />
              </View>
            </Marker>
            {ifMarkersExists ? memoizedMarkers : null}
          </MapView>
          {memoizedTags ? (
            <ScrollView
              horizontal={true}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                justifyContent: "center",
                alignItems: "baseline",
              }}
              style={{
                position: "absolute",
                marginRight: 10,
                height: 55,
              }}
            >
              {memoizedTags}
            </ScrollView>
          ) : null}
          <AnimatedPanel
            isPanelVisible={isPanelVisible}
            slideAnim={slideAnim}
            contentHeight={contentHeight}
            pan={pan}
            selectedMarker={selectedMarker}
            panResponder={panResponder}
            onContentLayout={onContentLayout}
            setContactOption={setContactOption}
            contactOpts={contactOpts}
            createCall={createCall}
          />
          <ContactModal
            contactOption={contactOption}
            setContactOption={setContactOption}
            selectedMarker={selectedMarker}
            isRescheduledCp={isRescheduledCp.current}
            storageKey="MAP_ACTION"
            createCall={createCall}
          />
          <SarvCallModal
            showSarvModal={showSarvModal}
            setShowSarvModal={setShowSarvModal}
          />
          <CallOptionsModal
            selectedMarker={selectedMarker}
            visible={isRescheduledCp.current && showCallOptionsModal}
            onClose={() => setShowCallOptionsModal(false)}
            setShowCallOptionsModal={setShowCallOptionsModal}
            setContactOption={setContactOption}
            refreshData={fetchData}
            callModalMode={"DEFAULT"}
            taskActionConfig={taskActionConfig}
          />
        </View>
      ) : null}
    </View>
  );
}

export default React.memo(OrdersMapView);
